import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ReportBuilder = ({ onGenerateReport, isGenerating = false }) => {
  const [reportConfig, setReportConfig] = useState({
    dateRange: 'last30days',
    startDate: '',
    endDate: '',
    teams: [],
    platforms: [],
    violationTypes: [],
    reportType: 'summary'
  });

  const dateRangeOptions = [
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'last90days', label: 'Last 90 days' },
    { value: 'custom', label: 'Custom range' }
  ];

  const teamOptions = [
    { value: 'marketing', label: 'Marketing Team' },
    { value: 'social', label: 'Social Media Team' },
    { value: 'content', label: 'Content Team' },
    { value: 'compliance', label: 'Compliance Team' }
  ];

  const platformOptions = [
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter/X' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'website', label: 'Website' }
  ];

  const violationTypeOptions = [
    { value: 'misleading', label: 'Misleading Claims' },
    { value: 'disclosure', label: 'Missing Disclosures' },
    { value: 'risk', label: 'Risk Warnings' },
    { value: 'regulatory', label: 'Regulatory Violations' },
    { value: 'advertising', label: 'Advertising Standards' }
  ];

  const reportTypeOptions = [
    { value: 'summary', label: 'Summary Report' },
    { value: 'detailed', label: 'Detailed Analysis' },
    { value: 'audit', label: 'Audit Trail' },
    { value: 'trends', label: 'Trend Analysis' }
  ];

  const handleInputChange = (field, value) => {
    setReportConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleGenerate = () => {
    onGenerateReport(reportConfig);
  };

  const isCustomDateRange = reportConfig?.dateRange === 'custom';

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Report Builder</h2>
          <p className="text-sm text-muted-foreground">Configure parameters to generate compliance reports</p>
        </div>
        <Icon name="Settings" size={20} className="text-muted-foreground" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Date Range Selection */}
        <div className="space-y-4">
          <Select
            label="Date Range"
            options={dateRangeOptions}
            value={reportConfig?.dateRange}
            onChange={(value) => handleInputChange('dateRange', value)}
          />
          
          {isCustomDateRange && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={reportConfig?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
              />
              <Input
                label="End Date"
                type="date"
                value={reportConfig?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
              />
            </div>
          )}
        </div>

        {/* Report Type */}
        <div>
          <Select
            label="Report Type"
            options={reportTypeOptions}
            value={reportConfig?.reportType}
            onChange={(value) => handleInputChange('reportType', value)}
          />
        </div>

        {/* Team Selection */}
        <div>
          <Select
            label="Teams"
            description="Select teams to include in the report"
            options={teamOptions}
            value={reportConfig?.teams}
            onChange={(value) => handleInputChange('teams', value)}
            multiple
            searchable
            clearable
          />
        </div>

        {/* Platform Selection */}
        <div>
          <Select
            label="Platforms"
            description="Select platforms to analyze"
            options={platformOptions}
            value={reportConfig?.platforms}
            onChange={(value) => handleInputChange('platforms', value)}
            multiple
            searchable
            clearable
          />
        </div>

        {/* Violation Types */}
        <div className="lg:col-span-2">
          <Select
            label="Violation Types"
            description="Filter by specific violation categories"
            options={violationTypeOptions}
            value={reportConfig?.violationTypes}
            onChange={(value) => handleInputChange('violationTypes', value)}
            multiple
            searchable
            clearable
          />
        </div>
      </div>
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Report will include data from selected parameters
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="Save"
            iconPosition="left"
          >
            Save Template
          </Button>
          <Button
            variant="default"
            iconName="FileText"
            iconPosition="left"
            loading={isGenerating}
            onClick={handleGenerate}
          >
            Generate Report
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;